/**
 * @fileoverview Utility functions for master equipment expansion calculations
 * @description Handles calculation of equipment instance expansion based on cable connections
 */

import type {
  MasterEquipmentConfig,
  MasterEquipmentExpansion,
  ExpandedEquipmentInstance,
} from '@/types/master-equipment.types'

/**
 * Calculate how many instances of master equipment are needed
 * based on cable connections and configured port count
 *
 * @param spaceEquipmentId - ID of the equipment in space
 * @param equipmentDefinitionId - ID of the equipment definition
 * @param equipmentName - Name of the equipment
 * @param totalCables - Total number of cables connected
 * @param config - Equipment configuration with ports per instance
 * @returns Expansion details or null if expansion disabled
 */
export function calculateEquipmentExpansion(
  spaceEquipmentId: string,
  equipmentDefinitionId: string,
  equipmentName: string,
  totalCables: number,
  config: MasterEquipmentConfig | null
): MasterEquipmentExpansion | null {
  // Return null if no configuration or expansion is disabled
  if (!config || !config.expansionEnabled) {
    return null
  }

  const portsPerInstance = config.portsPerInstance

  // Calculate number of instances needed
  const instanceCount = Math.max(1, Math.ceil(totalCables / portsPerInstance))

  // Calculate total ports
  const totalPorts = instanceCount * portsPerInstance

  // Create expanded instances
  const instances: ExpandedEquipmentInstance[] = []
  let remainingCables = totalCables

  for (let i = 0; i < instanceCount; i++) {
    const instanceNumber = i + 1
    const portRangeStart = 1 // Ports reset per instance
    const portRangeEnd = portsPerInstance
    const portsUsed = Math.min(remainingCables, portsPerInstance)

    instances.push({
      instanceNumber,
      portRangeStart,
      portRangeEnd,
      portsUsed,
      connectedCableIds: [], // Will be populated later by cable service
    })

    remainingCables -= portsUsed
  }

  return {
    spaceEquipmentId,
    equipmentDefinitionId,
    equipmentName,
    totalCables,
    portsPerInstance,
    instanceCount,
    totalPorts,
    instances,
    needsExpansion: true, // Always true when expansion is configured, even if only 1 instance
  }
}
